/**
 * Rewrites markdown tables as spoken sentences.
 *
 * Read-aloud is fed a document's markdown (or a chat message's), and `stripMarkdown` has no table
 * handling: pipes and the alignment row (`| --- | --- |`) reach the TTS voice verbatim, which reads
 * out as a run of dashes before any cell content. A BlockNote table also has no header cells, so its
 * GFM export carries an all-empty header row that is spoken as nothing but pipes.
 *
 * Each remaining row becomes one paragraph of sentences, one per non-empty cell. Row-as-paragraph
 * matters beyond punctuation: `chunkText` splits on blank lines first, so rows give it somewhere to
 * break other than mid-sentence, and dropping the column padding keeps a table from eating the
 * 4096-character budget.
 *
 * Idempotent — the output contains no pipe rows, so applying it twice is a no-op.
 */

/** A GFM row: starts with `|` and holds at least one more `|`, e.g. `| a | b |`. */
const isTableRowLine = (line: string): boolean => {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.indexOf('|', 1) !== -1;
};

/** Splits a row into trimmed cell texts, unescaping the `\|` that markdown uses for a literal pipe. */
const splitRowCells = (line: string): string[] => {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split(/(?<!\\)\|/).map((cell) => cell.replace(/\\\|/g, '|').trim());
};

/** The `| --- | :---: |` alignment row, which carries no content. */
const isAlignmentRow = (cells: string[]): boolean => cells.length > 0 && cells.every((cell) => /^:?-{1,}:?$/.test(cell));

const SENTENCE_END = /[.!?:;]$/;

/** One row as a sentence per cell, so the voice pauses between cells instead of running them together. */
const rowToSentences = (cells: string[]): string =>
  cells
    .filter((cell) => cell.length > 0)
    .map((cell) => (SENTENCE_END.test(cell) ? cell : `${cell}.`))
    .join(' ');

const tableToSpeech = (rows: string[]): string[] =>
  rows
    .map(splitRowCells)
    .filter((cells) => !isAlignmentRow(cells))
    .map(rowToSentences)
    .filter((sentences) => sentences.length > 0);

export const markdownTablesToSpeech = (text: string): string => {
  if (!text.includes('|')) {
    return text;
  }

  const lines = text.split('\n');
  const result: string[] = [];
  let pendingRows: string[] = [];

  const flushRows = () => {
    if (pendingRows.length === 0) {
      return;
    }
    // A lone pipe line is far more likely to be prose than a table, so leave it alone.
    if (pendingRows.length === 1) {
      result.push(...pendingRows);
    } else {
      const spokenRows = tableToSpeech(pendingRows);
      if (spokenRows.length > 0) {
        // Blank lines around and between rows: `chunkText` breaks on those first.
        result.push('', ...spokenRows.flatMap((row) => [row, '']));
      }
    }
    pendingRows = [];
  };

  for (const line of lines) {
    if (isTableRowLine(line)) {
      pendingRows.push(line);
      continue;
    }
    flushRows();
    result.push(line);
  }
  flushRows();

  return result
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
